import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { getSubscriptionFeatures } from "@/services/subscriptionService";

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [features, setFeatures] = useState(null);
  const [isPaidOnline, setIsPaidOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [planName, setPlanName] = useState("");
  const [planId, setPlanId] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    if (user?.subscription) {
      setSubscriptionStatus(user.subscription.status);

      if (["past_due", "paused"].includes(user.subscription.status)) {
        setWarning({
          status: user.subscription.status,
          severity: "soft",
        });
      }

      setIsBlocked(
        ["expired", "cancelled", "suspended", "trial_expired"].includes(
          user.subscription.status
        )
      );
    }

    if (user && user.role !== "super_admin") {
      fetchSubscriptionFeatures();
    } else if (user && user.role === "super_admin") {
      // Super admin has all features
      setFeatures(getAllFeatures());
      setPlanName("Super Admin");
      setPlanId("super-admin");
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const handleBlocked = (e) => {
      setSubscriptionStatus(e.detail);
      setIsBlocked(true);
      setWarning(null);
    };

    const handleRequired = () => {
      setSubscriptionStatus("none");
      setIsBlocked(true);
      setWarning(null);
    };

    const handleWarning = (e) => {
      setSubscriptionStatus(e.detail);
      setWarning({
        status: e.detail.status,
        severity: "soft",
      });
    };

    const handleActive = (e) => {
      setSubscriptionStatus(e.detail);
      setWarning(null);
      setIsBlocked(false);
    };

    window.addEventListener("subscription-blocked", handleBlocked);
    window.addEventListener("subscription-required", handleRequired);
    window.addEventListener("subscription-warning", handleWarning);
    window.addEventListener("subscription-active", handleActive);

    return () => {
      window.removeEventListener("subscription-blocked", handleBlocked);
      window.removeEventListener("subscription-required", handleRequired);
      window.removeEventListener("subscription-warning", handleWarning);
      window.removeEventListener("subscription-active", handleActive);
    };
  }, []);

  const getAllFeatures = () => {
    return {
      employee_limit: 5,
      admin_users_limit: 1,
      trial_days: 14,

      custom_branding: true,
      multiple_branches: true,
      role_based_access: true,
      activity_logs: true,
      ip_restrictions: true,

      document_upload: true,
      department_mapping: true,
      bulk_import: true,
      excel_pdf_export: true,

      attendance_dashboard: true,
      web_punch: true,
      geo_fencing: true,
      biometric_integration: true,
      multi_shift_support: true,
      regularization_requests: true,
      late_early_punch_rules: true,
      performance_management: true,
      basic_leave_management: true,
      custom_leave_management: true,
      leave_encashment: true,

      payroll_analytics: true,
      basic_payroll: true,
      advanced_payroll: true,
      bank_advice: true,
      multi_bank_accounts: true,
      loan_management: true,

      pf_calculation: true,
      esi_calculation: true,
      pt_slabs: true,
      tds_regimes: true,
      lwf_calculation: true,
      form_16_generation: true,
    };
  };

  const fetchSubscriptionFeatures = async () => {
    try {
      const response = await getSubscriptionFeatures();

      setFeatures(response.features);
      setIsPaidOnline(response.is_paid_online)
      setPlanName(response.plan_name);
      setPlanId(response.plan_id);
    } catch (error) {
      console.error("Error fetching subscription features:", error);
      setFeatures({
        employee_limit: 5,
        admin_users_limit: 1,
        trial_days: 14,

        custom_branding: false,
        multiple_branches: false,
        role_based_access: false,
        activity_logs: false,
        ip_restrictions: false,

        document_upload: false,
        department_mapping: false,
        bulk_import: true,
        excel_pdf_export: false,

        attendance_dashboard: true,
        web_punch: false,
        geo_fencing: false,
        biometric_integration: false,
        multi_shift_support: false,
        regularization_requests: false,
        late_early_punch_rules: false,
        performance_management: false,
        basic_leave_management: true,
        custom_leave_management: false,
        leave_encashment: false,

        payroll_analytics: true,
        basic_payroll: false,
        advanced_payroll: true,
        bank_advice: true,
        multi_bank_accounts: true,
        loan_management: true,

        pf_calculation: true,
        esi_calculation: true,
        pt_slabs: true,
        tds_regimes: true,
        lwf_calculation: true,
        form_16_generation: false,
      });
      setPlanName("Free");
      setPlanId("free");
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (featureCheck) => {
    // If the input is a single string (standard case)
    if (typeof featureCheck === "string") {
      if (!features) return false;
      return features[featureCheck] === true;
    }

    // If the input is an array (the new requirement)
    if (Array.isArray(featureCheck)) {
      // If the array is empty, assume feature is required (or handle as desired)
      if (featureCheck.length === 0) return true;

      // Use .some() to check if AT LEAST ONE feature in the array is true
      return featureCheck.some((featureName) => {
        if (!features) return false;
        return features[featureName] === true;
      });
    }

    // If input is neither string nor array, assume no feature required (or false)
    return true;
  };

  const value = {
    features,
    isPaidOnline,
    planName,
    planId,
    subscriptionStatus,
    isBlocked,
    warning,
    loading,
    hasFeature,
    refetchFeatures: fetchSubscriptionFeatures
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
